/**
 * security.js — Frontend link-whitelisting utilities
 *
 * Mirror of the domain/scheme whitelist in api/services/routingEngine.js.
 * Applied to any href value that originates from server-provided data
 * (JustWatch directUrls, native appUrls) rather than hardcoded constants,
 * so that a compromised upstream API cannot inject arbitrary navigation targets.
 */

// Streaming-service hostnames whose HTTPS URLs are safe to navigate to.
const ALLOWED_DOMAINS = new Set([
  'netflix.com',
  'hulu.com',
  'play.max.com',
  'hbo.com',
  'disneyplus.com',
  'amazon.com',
  'primevideo.com',
  'tv.apple.com',
  'itunes.apple.com',
  'peacocktv.com',
  'paramountplus.com',
  'youtube.com',
  'vudu.com',
  'play.google.com',
  'app.plex.tv',
  'watch.spectrum.net',
  'ko-fi.com',
])

// Native app URI scheme prefixes used by streaming apps.
const ALLOWED_SCHEMES = [
  'nflx://',
  'hulu:///',
  'hulu://',
  'peacock:///',
  'paramountplus:///',
  'youtube://',
  'plex://',
]

/**
 * safeLinkHref(url, fallback?)
 *
 * Returns url if it passes the streaming-service whitelist; otherwise returns
 * fallback (default: null). Accepts HTTPS URLs whose hostname matches an allowed
 * domain (exact match or subdomain) or a known native app URI scheme.
 *
 * Usage: replace raw `directUrls[s.id]` and `appUrls[s.id]` accesses with this.
 */
export function safeLinkHref(url, fallback = null) {
  if (!url || typeof url !== 'string') return fallback

  for (const scheme of ALLOWED_SCHEMES) {
    if (url.startsWith(scheme)) return url
  }

  try {
    const u = new URL(url)
    if (u.protocol !== 'https:') return fallback
    const hostname = u.hostname.toLowerCase()
    for (const domain of ALLOWED_DOMAINS) {
      if (hostname === domain || hostname.endsWith('.' + domain)) return url
    }
  } catch {}

  return fallback
}

/**
 * safeExternalHref(url, fallback?)
 *
 * Lighter check for operator-controlled external links (VPN affiliate, etc.)
 * that cannot be strictly domain-whitelisted. Rejects anything that isn't a
 * plain HTTPS URL (blocks javascript:, data:, and HTTP downgrade).
 */
export function safeExternalHref(url, fallback = null) {
  if (!url || typeof url !== 'string') return fallback
  try {
    const u = new URL(url)
    return u.protocol === 'https:' ? url : fallback
  } catch {}
  return fallback
}
